import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { SectionHeader } from "@/components/ui";
import CheckDialog from "../components/checks/check-dialog.tsx";
import CheckRunsTable from "../components/checks/check-runs-table.tsx";
import { PageHeader } from "../components/page-header";
import { RunStatusChip } from "../components/run-status-chip.tsx";
import { VariableInput } from "../components/variable-input.tsx";
import { VariableMonacoEditor } from "../components/variable-monaco-editor.tsx";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useTypedQueryParams } from "../hooks/use-query-params.ts";
import { STATUS_COLORS } from "../theme/theme";
import type { ApiConfig, Check, CheckSchema, Test, TestVariable } from "../types.ts";

interface CheckDetailsTabs {
  tab?: "config" | "runs";
  runId?: string;
}

interface ChecksListResponse {
  checks: Check[];
  total: number;
}

export default function CheckDetailsPage() {
  const { checkId } = useParams();
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { params, setParam, removeParam } = useTypedQueryParams<CheckDetailsTabs>();
  const tab = params.tab ?? "config";
  const selectedRunId = params.runId;

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setParam("tab", newValue as CheckDetailsTabs["tab"]);
  };

  const handleRunSelect = (runId: string) => {
    setParam("runId", runId);
  };

  const handleCloseRunDrawer = () => {
    removeParam("runId");
  };

  // Auto-switch to runs tab when runId is provided via URL
  useEffect(() => {
    if (selectedRunId && tab !== "runs") {
      setParam("tab", "runs");
    }
  }, [selectedRunId, tab, setParam]);

  // Edit dialog state
  const [editData, setEditData] = useState<CheckSchema | Check | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const {
    data: check,
    isLoading: isLoadingCheck,
    refetch: refetchCheck,
  } = useApiQuery<Check>(`/checks/${checkId}`, {
    enabled: !!checkId,
    refetchInterval: 5000,
  });

  // Fetch app configs to get the app config name
  const { data: apiConfig } = useApiQuery<ApiConfig>("/config");
  const appConfig = apiConfig?.configs?.find((c) => c.id === check?.app_config_id);

  // Fetch test dependency if configured
  const { data: testDependency } = useApiQuery<Test>(`/tests/${check?.test_dependency_id}`, {
    enabled: !!check?.test_dependency_id,
  });

  // Fetch sibling checks to resolve check_dependency_ids to names.
  const { data: siblingChecksResponse } = useApiQuery<ChecksListResponse>(
    check?.app_config_id ? `/checks?app_config_id=${check.app_config_id}` : "/checks",
    {
      enabled: !!check?.app_config_id && (check?.check_dependency_ids?.length || 0) > 0,
    },
  );

  // Resolved dependency checks, in the order declared on the check.
  const dependencyChecks = useMemo(() => {
    const byId = new Map((siblingChecksResponse?.checks ?? []).map((c) => [c.id, c]));
    return (check?.check_dependency_ids ?? []).map((id) => ({
      id,
      check: byId.get(id),
    }));
  }, [siblingChecksResponse, check?.check_dependency_ids]);

  // Fetch global variables
  const { data: globalVariables } = useApiQuery<TestVariable[]>("/global-variables");

  // Extract variables from app config for display
  const allVariables = useMemo((): TestVariable[] => {
    const variables: TestVariable[] = [];

    // Add global variables first (highest precedence)
    if (globalVariables) {
      variables.push(...globalVariables);
    }

    if (appConfig) {
      if (appConfig.base_url) {
        variables.push({
          key: "base_url",
          value: appConfig.base_url,
          type: "url",
          lifetime: "all",
          context: "app_config",
          is_sensitive: false,
        });
      }
      if (appConfig.login_url) {
        variables.push({
          key: "login_url",
          value: appConfig.login_url,
          type: "url",
          lifetime: "all",
          context: "app_config",
          is_sensitive: false,
        });
      }
      if (appConfig.login_username) {
        variables.push({
          key: "login_username",
          value: appConfig.login_username,
          type: "username",
          lifetime: "all",
          context: "app_config",
          is_sensitive: false,
        });
      }
      if (appConfig.login_password) {
        variables.push({
          key: "login_password",
          value: appConfig.login_password,
          type: "password",
          lifetime: "all",
          context: "app_config",
          is_sensitive: true,
        });
      }
      if (appConfig.login_mfa_secret) {
        variables.push({
          key: "login_mfa_secret",
          value: appConfig.login_mfa_secret,
          type: "sensitive",
          lifetime: "all",
          context: "app_config",
          is_sensitive: true,
        });
      }
    }

    // Add check-specific variables
    if (check?.variables) {
      variables.push(...check.variables);
    }

    return variables;
  }, [globalVariables, appConfig, check?.variables]);

  const { mutate: updateCheck, isPending: isUpdatingCheck } = useApiMutation<
    Partial<CheckSchema>,
    { status: string }
  >("PATCH", `/checks/${checkId}`, {
    onSuccess: () => {
      enqueueSnackbar("Check updated!", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      refetchCheck();
      setEditData(null);
    },
  });

  const { mutate: runCheck, isPending: isRunningCheck } = useApiMutation<void, { run_id: string }>(
    "POST",
    `/checks/${checkId}/run`,
    {
      onSuccess: () => {
        enqueueSnackbar(
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="body1">Check run started!</Typography>
            <IconButton
              aria-label="View runs"
              onClick={() => setParam("tab", "runs")}
              size="small"
              color="inherit"
            >
              <OpenInNewIcon sx={{ color: "common.white" }} />
            </IconButton>
          </Stack>,
          {
            variant: "success",
            anchorOrigin: {
              horizontal: "right",
              vertical: "bottom",
            },
          },
        );
        setParam("tab", "runs");
        refetchCheck();
        // Immediately refetch runs to show the new running check
        queryClient.invalidateQueries({
          queryKey: [`/checks/${checkId}/runs`],
        });
      },
    },
  );

  const { mutate: deleteCheck, isPending: isDeletingCheck } = useApiMutation<void, void>(
    "DELETE",
    `/checks/${checkId}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Check deleted!", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        nav("/checks");
      },
    },
  );

  const handleEdit = () => {
    if (check) {
      setEditData(check);
    }
  };

  const handleSaveEdit = (data: CheckSchema) => {
    updateCheck(data);
  };

  const handleDeleteConfirm = () => {
    deleteCheck({});
  };

  if (isLoadingCheck) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading check...</Typography>
      </Box>
    );
  }

  if (!check) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Check not found</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        flexDirection: "column",
        display: "flex",
        overflow: "hidden",
      }}
    >
      <PageHeader
        title={
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                letterSpacing: "-0.02em",
              }}
            >
              {check.name}
            </Typography>
            {check.run_status && <RunStatusChip status={check.run_status} />}
            <Chip
              label={check.check_type.toUpperCase()}
              size="small"
              sx={{
                fontSize: "0.65rem",
                height: 20,
                backgroundColor: STATUS_COLORS.info.bg,
                color: STATUS_COLORS.info.text,
              }}
            />
          </Stack>
        }
        subtitle={check.description || "API health check"}
        actions={
          <>
            <Tooltip title="Edit check" placement="top">
              <IconButton
                size="small"
                onClick={handleEdit}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "info.dark",
                    backgroundColor: "common.white",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete check" placement="top">
              <IconButton
                size="small"
                onClick={() => setDeleteConfirmOpen(true)}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  color: "error.dark",
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: STATUS_COLORS.error.bg,
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Run check now" placement="top">
              <IconButton
                size="small"
                disabled={isRunningCheck}
                onClick={() => runCheck({})}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "info.dark",
                    backgroundColor: "common.white",
                  },
                }}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        }
      />

      {/* Edit Dialog */}
      {editData && (
        <CheckDialog
          open={!!editData}
          mode="edit"
          onClose={() => setEditData(null)}
          onSave={handleSaveEdit}
          data={editData}
          setData={setEditData}
          isProcessing={isUpdatingCheck}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Check Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the check "{check.name}"? This action will soft delete
            the check and it will no longer be visible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeletingCheck}>
            {isDeletingCheck ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleTabChange} aria-label="Check details tabs">
            <Tab label="Configuration" value="config" />
            <Tab label="Run History" value="runs" />
          </TabList>
        </Box>

        <TabPanel value="config" sx={{ flex: 1, overflow: "auto" }}>
          <Stack spacing={3}>
            {/* App Configuration */}
            {appConfig && (
              <Paper sx={{ p: 3 }}>
                <SectionHeader>App Configuration</SectionHeader>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {appConfig.name}
                    </Typography>
                  </Grid>
                  {appConfig.base_url && (
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Typography variant="caption" color="text.secondary">
                        Base URL
                      </Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ wordBreak: "break-all" }}>
                        {appConfig.base_url}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  Variables from this config (e.g., {"{{base_url}}"}, {"{{login_username}}"}) are
                  automatically substituted at runtime.
                </Typography>
              </Paper>
            )}

            {/* API Configuration */}
            <Paper sx={{ p: 3 }}>
              <SectionHeader>API Configuration</SectionHeader>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Method
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {check.config?.method || "GET"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 10 }}>
                  <VariableInput
                    label="URL"
                    value={check.config?.url || ""}
                    onChange={() => {}}
                    variables={allVariables}
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                {check.config?.headers && Object.keys(check.config.headers).length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <VariableMonacoEditor
                      label="Headers"
                      value={JSON.stringify(check.config.headers, null, 2)}
                      onChange={() => {}}
                      variables={allVariables}
                      readOnly
                      height={100}
                    />
                  </Grid>
                )}
                {check.config?.body && (
                  <Grid size={{ xs: 12 }}>
                    <VariableInput
                      label="Request Body"
                      value={check.config.body}
                      onChange={() => {}}
                      variables={allVariables}
                      multiline
                      minRows={3}
                      size="small"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Validation Configuration */}
            {check.validation && (
              <Paper sx={{ p: 3 }}>
                <SectionHeader>Validation Rules</SectionHeader>
                <Grid container spacing={2}>
                  {check.validation.expected_status_code && (
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        Expected Status Code
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {check.validation.expected_status_code}
                      </Typography>
                    </Grid>
                  )}
                  {check.validation.expected_body_contains && (
                    <Grid size={{ xs: 12, md: 8 }}>
                      <VariableInput
                        label="Response Body Contains"
                        value={check.validation.expected_body_contains}
                        onChange={() => {}}
                        variables={allVariables}
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  )}
                  {check.validation.json_path_assertions &&
                    check.validation.json_path_assertions.length > 0 && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary">
                          JSONPath Assertions
                        </Typography>
                        <Stack spacing={1} mt={1}>
                          {check.validation.json_path_assertions.map((assertion, index) => (
                            <Box
                              key={index}
                              sx={{
                                p: 1.5,
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                backgroundColor: "background.default",
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <Typography
                                  variant="body2"
                                  fontFamily="monospace"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {assertion.path}
                                </Typography>
                                <Chip label={assertion.type} size="small" variant="outlined" />
                                {assertion.type !== "exists" && assertion.expected && (
                                  <Typography variant="body2" color="text.secondary">
                                    "{assertion.expected}"
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                    )}
                  {check.validation.ai_validation && (
                    <Grid size={{ xs: 12 }}>
                      <VariableInput
                        label="AI Validation Prompt"
                        value={check.validation.ai_validation}
                        onChange={() => {}}
                        variables={allVariables}
                        multiline
                        minRows={2}
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                      <Chip
                        label={`${check.validation.ai_mode} mode`}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            {/* Retry Configuration */}
            <Paper sx={{ p: 3 }}>
              <SectionHeader>Retry & Timeout Settings</SectionHeader>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Retry Strategy
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {check.retry_strategy}
                  </Typography>
                </Grid>
                {check.retry_strategy !== "none" && (
                  <>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Max Retries
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {check.max_retries}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Retry Delay
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {check.retry_delay_seconds}s
                      </Typography>
                    </Grid>
                    {check.retry_strategy === "exponential" && (
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Backoff Multiplier
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {check.backoff_multiplier}x
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Timeout
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {check.timeout_ms}ms
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Test Dependency */}
            {check.test_dependency_id && (
              <Paper sx={{ p: 3 }}>
                <SectionHeader>Test Dependency</SectionHeader>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" color="text.secondary">
                      Dependency Test
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" fontWeight={500}>
                        {testDependency?.name || "Loading..."}
                      </Typography>
                      {testDependency && (
                        <Tooltip title="View test">
                          <IconButton
                            size="small"
                            onClick={() => nav(`/tests/${check.test_dependency_id}`)}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" color="text.secondary">
                      Token Cache TTL
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {check.dependency_cache_ttl_seconds
                        ? `${check.dependency_cache_ttl_seconds} seconds`
                        : "Auto-detect from JWT"}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  This test runs before each check execution to generate fresh auth tokens. Token
                  values are cached based on JWT expiration or the configured TTL.
                </Typography>
              </Paper>
            )}

            {/* Chaining: Dependencies & Output Captures */}
            {((check.check_dependency_ids?.length || 0) > 0 ||
              (check.output_captures?.length || 0) > 0 ||
              check.alias) && (
              <Paper sx={{ p: 3 }}>
                <SectionHeader>Chaining</SectionHeader>
                <Stack spacing={3}>
                  {check.alias && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Alias
                      </Typography>
                      <Typography variant="body1" fontWeight={500} fontFamily="monospace">
                        {check.alias}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Namespace for response variables exposed to dependent checks.
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Dependencies
                    </Typography>
                    {dependencyChecks.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        None
                      </Typography>
                    ) : (
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {dependencyChecks.map(({ id, check: dep }) => (
                          <Stack key={id} direction="row" spacing={1} alignItems="center">
                            <Typography variant="body1" fontWeight={500}>
                              {dep?.name || `Check ${id.slice(0, 8)}`}
                            </Typography>
                            <Tooltip title="View check">
                              <IconButton size="small" onClick={() => nav(`/checks/${id}`)}>
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Checks that must run before this one inside a schedule.
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Output Captures
                    </Typography>
                    {(check.output_captures?.length || 0) === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        None
                      </Typography>
                    ) : (
                      <Stack spacing={1} sx={{ mt: 0.5 }}>
                        {check.output_captures?.map((capture, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 1.5,
                              border: 1,
                              borderColor: "divider",
                              borderRadius: 1,
                              backgroundColor: "background.default",
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Chip
                                label={`{{${capture.name}}}`}
                                size="small"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.7rem",
                                  height: 22,
                                  backgroundColor: STATUS_COLORS.info.bg,
                                  color: STATUS_COLORS.info.text,
                                }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                ←
                              </Typography>
                              <Typography variant="body2" fontFamily="monospace">
                                {capture.json_path}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Named values extracted from this check's response, available as variables to
                      dependent checks.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Stack>
        </TabPanel>

        <TabPanel value="runs" sx={{ flex: 1, overflow: "hidden", p: 0 }}>
          <CheckRunsTable
            checkId={checkId!}
            selectedRunId={selectedRunId}
            onRunSelect={handleRunSelect}
            onCloseRunDrawer={handleCloseRunDrawer}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
}
